import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type {
  BrandBrainProduct,
  BrandBrainTestimonial,
  BrandBrainVoiceExample,
} from '@magig/schemas';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useMemo, useState } from 'react';
import { OnboardingBack, OnboardingExit, PageEyebrow } from '../components/auth-shells.js';
import {
  ProductsReviewList,
  TestimonialsReviewList,
  useReviewActions,
  VoiceExamplesReviewList,
} from '../components/brain-review-lists.js';
import { Mag } from '../components/mag.js';
import {
  type BrandBrainEnvelope,
  type ExtractionStep,
  type ExtractionStepRow,
  type ExtractionStepStatus,
  fetchExtractionProgress,
  type MeResponse,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { brandBrainQuery, brandsQuery, meQuery } from '../lib/queries.js';

// Onboarding step 3 — "confirm what mag pulled". Mirrors the step-2
// review surface but focused on the item-level sections that landed
// via the slower background extractors: products + services,
// testimonials, and voice snippets. The founder accepts drafts
// inline (status: draft → auto) or discards rows mag mis-extracted.
// Identity / voice persona / customer / team live on step 2.
//
// 54h.11 — voice snippets used to surface only on /home via
// BrainDraftsReview, which read as "an extra task after onboarding."
// pulling them into step 3 next to testimonials lets the founder
// close the brain-review loop in one pass.
//
// The visual-scrape + sub-page crawl + extractors all run in the
// background. On a fast brand they finish before the founder reaches
// this step; on a slow brand we show a loader and poll.

interface ConfirmSearch {
  onboarding?: '1' | undefined;
}

// The steps this screen waits on, with the label the founder sees.
// Doubles as the filter: a step absent from this map is not rendered
// and does not gate the CTA. That deliberately excludes step 1's
// fetch/identity/voice/customer group (already shown + confirmed
// there) and the two deferred image classifiers below.
//
// Labels name the ARTIFACT, not the machinery. The row this replaced
// read "colors, fonts, logo, brand visual system", which is why
// founders asked why mag was re-reading step 1: colors/fonts/logo are
// the tier-1 HTML pass and were already on screen by then. The only
// thing genuinely pending here is the tier-2 vision read — the
// palette-with-roles, type pairing, motifs and density that CSS can't
// express — so that is what the row is now called.
const STEP_LABELS: Partial<Record<ExtractionStep, string>> = {
  visual_screenshot: 'screenshots',
  visual_read_guidelines: 'colors + type',
  visual_store_logo: 'logo',
  visual_store_favicon: 'favicon',
  visual_upsert_guidelines: 'visual system',
  crawl_subpages: 'sub-pages',
  crawl_persist_pages: 'page text',
  extract_products: 'products',
  extract_team: 'team',
  extract_testimonials: 'testimonials',
  extract_voice_examples: 'voice samples',
  extract_design_system: 'design system',
  extract_motifs: 'motifs',
};

// Image classification is the long tail — one vision call per image,
// ~1-2 minutes. It only needs to land in the brain for later creative
// work, never for the founder to finish onboarding, so it is excluded
// from STEP_LABELS above and surfaced as a quiet background line.
// Same call step 1 makes.
const DEFERRED_STEPS: ReadonlyArray<ExtractionStep> = [
  'visual_classify_landing_images',
  'crawl_classify_subpage_images',
];

/** A step is resolved once it can no longer change. `skipped` counts:
 *  the orchestrator skips a step when its input never arrived, which
 *  is an answer, not a pending state. */
function isResolved(status: ExtractionStepStatus): boolean {
  return status === 'done' || status === 'failed' || status === 'skipped';
}

export const Route = createFileRoute('/brand/$slug/brief/confirm')({
  validateSearch: (search: Record<string, unknown>): ConfirmSearch =>
    search['onboarding'] === '1' ? { onboarding: '1' } : {},
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    const envelope = await queryClient.ensureQueryData(brandBrainQuery(brand.id));
    return { me, envelope, brand };
  },
  loader: ({ context }) =>
    context as {
      me: MeResponse;
      envelope: BrandBrainEnvelope | null;
      brand: { id: string; name: string; slug: string };
    },
  component: ConfirmStep,
});

function ConfirmStep() {
  const { t } = useLingui();

  const navigate = useNavigate();
  const { brand } = Route.useLoaderData();
  useBrandTitle(brand.slug, ['brief · confirm']);
  const { data: brands } = useQuery(brandsQuery());
  const showBrandLabel = (brands?.filter((b) => b.archivedAt === null).length ?? 0) > 1;

  // Refetch the brain every 4s while the founder is on this page —
  // the background extractors land in waves (visual first ~30-47s,
  // then crawl + products + team + testimonials + voice samples
  // over another ~30s). Polling stops automatically once the
  // founder navigates away.
  const { data: envelope } = useQuery({
    ...brandBrainQuery(brand.id),
    refetchInterval: 4_000,
    refetchIntervalInBackground: false,
  });

  const brain = envelope?.brandBrain ?? null;

  // Readiness comes from the orchestrator's own progress rows — the
  // same feed step 1 renders — rather than from guessing at the brain's
  // shape. The two booleans this replaced were:
  //
  //   visualsReady = Boolean(brain?.guidelines?.visualSystem)
  //   crawlReady   = any of team/products/testimonials/voiceExamples
  //
  // Both inferred "did the work finish?" from "did the work produce
  // output?", which are not the same question. visualSystem is null
  // whenever the vision pass is skipped — and it's skipped whenever
  // there are no screenshots to read — so an R2 outage left this
  // screen spinning FOREVER with no timeout and no error. crawlReady
  // had the mirror-image flaw its own comment conceded: a brand with
  // nothing extractable never flips it. Progress rows carry real
  // terminal states (done / failed / skipped), so every path ends.
  const { data: progress, isPending: progressPending } = useQuery({
    queryKey: ['extraction-progress', brand.id],
    queryFn: () => fetchExtractionProgress(brand.id),
    refetchInterval: (q) => {
      const rows = (q.state.data?.steps ?? []).filter((s) => s.step in STEP_LABELS);
      if (rows.length === 0) return false;
      return rows.some((s) => s.status === 'pending' || s.status === 'running') ? 1500 : false;
    },
    refetchIntervalInBackground: false,
  });

  // Only the steps this screen is actually waiting on. Step 1 already
  // showed the fetch/identity/voice/customer group; the deferred image
  // classifiers are excluded deliberately (see DEFERRED_STEPS).
  const progressRows = useMemo(
    () => (progress?.steps ?? []).filter((s) => s.step in STEP_LABELS),
    [progress],
  );

  const deferredRows = useMemo(
    () => (progress?.steps ?? []).filter((s) => DEFERRED_STEPS.includes(s.step)),
    [progress],
  );

  const fullyExtracted = useMemo(() => {
    // Still waiting on the first response — hold the reading state so
    // the content doesn't flash in and back out.
    if (progressPending) return false;
    // No rows at all means no run is being tracked (an older brand, or
    // rows aged out). There is nothing to wait for, so never block —
    // the previous code would have sat on a spinner indefinitely here.
    if (progressRows.length === 0) return true;
    return progressRows.every((s) => isResolved(s.status));
  }, [progressPending, progressRows]);

  // Per-row accept / discard mutations shared across the two list
  // cards. Same queueRef-serialized pattern as step 2.
  const actions = useReviewActions(brand.id);
  const [openSection, setOpenSection] = useState<
    'products' | 'testimonials' | 'voiceExamples' | null
  >(null);
  const toggle = (key: 'products' | 'testimonials' | 'voiceExamples') =>
    setOpenSection((cur) => (cur === key ? null : key));

  // Draft rows still want the founder's ok. We surface the count on each
  // collapsed section so showcase-first doesn't bury "these need review"
  // — the actual accept/discard lives in the expanded list.
  const draftCount = (items: ReadonlyArray<{ status: string }>) =>
    items.filter((i) => i.status === 'draft').length;

  const handleFinish = async () => {
    // Goes to the assets review step (54b/N — new step in the flow:
    // founder reviews + curates the images mag pulled from their site
    // before competition analysis fires). The previous direct hop to
    // competition is gone — assets step is the new gateway.
    await navigate({
      to: '/brand/$slug/onboarding/assets',
      params: { slug: brand.slug },
      search: { onboarding: '1' as const },
    });
  };

  return (
    <main className="relative min-h-screen bg-[var(--cream)]">
      <OnboardingBack
        to="/brand/$slug/brief"
        brandSlug={brand.slug}
        search={{ onboarding: '1' as const }}
      >
        <Trans>back to review</Trans>
      </OnboardingBack>
      <OnboardingExit brandSlug={brand.slug} />

      <section className="mx-auto max-w-[720px] px-4 py-12 sm:px-6">
        {/* ── Hero: Mag presents the deeper read (matches the brief reveal) ── */}
        <header className="flex flex-col items-start gap-4">
          <div
            className="mag-stagger-in flex items-center gap-3"
            style={{ animationDelay: '40ms' }}
          >
            <div className="relative">
              <div
                aria-hidden="true"
                className="mag-aura pointer-events-none absolute inset-[-14px] rounded-full"
              />
              <div className="mag-breathe relative">
                <Mag size={56} mood="casting" trackCursor />
              </div>
            </div>
            <PageEyebrow>
              <Trans>
                onboarding · step 3 of 6 · the rest
                {showBrandLabel ? ` · ${brand.name.toLowerCase()}` : ''}
              </Trans>
            </PageEyebrow>
          </div>
          <h1
            className="mag-stagger-in font-display text-[40px] sm:text-[60px] font-black leading-[0.98] tracking-[-0.045em]"
            style={{ animationDelay: '140ms' }}
          >
            {fullyExtracted ? (
              <Trans>
                what you sell,
                <br />
                <span className="text-[var(--plum)]">in your own words.</span>
              </Trans>
            ) : (
              <Trans>
                still reading
                <br />
                <span className="text-[var(--plum)]">the deeper parts…</span>
              </Trans>
            )}
          </h1>
          <p
            className="mag-stagger-in max-w-[540px] font-serif text-xl leading-tight text-[var(--ink)]/80"
            style={{ animationDelay: '220ms' }}
          >
            {fullyExtracted ? (
              <Trans>
                “your products, the quotes i found, and how you actually talk. leave what’s right —
                review + edit any section that needs it.”
              </Trans>
            ) : (
              <Trans>
                “i’m still reading the deeper parts of your site. give me a few seconds.”
              </Trans>
            )}
          </p>
        </header>

        {!fullyExtracted ? (
          <div className="mag-stagger-in mt-10" style={{ animationDelay: '320ms' }}>
            <StillReading rows={progressRows} deferred={deferredRows} />
          </div>
        ) : (
          <div className="mt-10 flex flex-col gap-4">
            <RevealSection
              label={t`products + services`}
              index={0}
              open={openSection === 'products'}
              onToggle={() => toggle('products')}
              pendingCount={draftCount(brain?.products ?? [])}
              showcase={<ProductsShowcase products={brain?.products ?? []} />}
            >
              <ProductsReviewList items={brain?.products ?? []} actions={actions} />
            </RevealSection>

            <RevealSection
              label={t`testimonials`}
              index={1}
              open={openSection === 'testimonials'}
              onToggle={() => toggle('testimonials')}
              pendingCount={draftCount(brain?.testimonials ?? [])}
              showcase={<TestimonialsShowcase testimonials={brain?.testimonials ?? []} />}
            >
              <TestimonialsReviewList items={brain?.testimonials ?? []} actions={actions} />
            </RevealSection>

            <RevealSection
              label={t`voice snippets`}
              index={2}
              open={openSection === 'voiceExamples'}
              onToggle={() => toggle('voiceExamples')}
              pendingCount={draftCount(brain?.voiceExamples ?? [])}
              showcase={<VoiceSnippetsShowcase snippets={brain?.voiceExamples ?? []} />}
            >
              <VoiceExamplesReviewList items={brain?.voiceExamples ?? []} actions={actions} />
            </RevealSection>
          </div>
        )}

        <div
          className="mag-stagger-in mt-10 flex items-center justify-end gap-3"
          style={{ animationDelay: '560ms' }}
        >
          <button type="button" onClick={handleFinish} className="btn btn-lg">
            {fullyExtracted ? <Trans>next · assets</Trans> : <Trans>skip for now</Trans>}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>
    </main>
  );
}

// ─── RevealSection ─────────────────────────────────────────────────
// Showcase of what Mag found, by default; the per-row accept/discard
// list is tucked behind a quiet "review + edit" toggle. Mirrors the
// brief reveal's RevealCard so all four post-research screens read the
// same.

function RevealSection({
  label,
  index,
  open,
  onToggle,
  pendingCount,
  showcase,
  children,
}: {
  label: string;
  index: number;
  open: boolean;
  onToggle: () => void;
  pendingCount: number;
  showcase: ReactNode;
  children: ReactNode;
}) {
  const hasPending = pendingCount > 0;
  return (
    <article
      className="mag-stagger-in rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6"
      style={{ animationDelay: `${320 + index * 80}ms` }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            ↘ {label}
          </p>
          {/* Draft rows still want the founder's ok — surface the count so
              showcase-first doesn't bury the review. Soft amber, not a
              danger pulse. */}
          {!open && hasPending ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#d4a017]/40 bg-[#d4a017]/12 px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.08em] text-[#7a5a05]">
              <Trans>
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#d4a017]" />
                {pendingCount} to confirm
              </Trans>
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={`shrink-0 font-mono text-[10.5px] font-bold uppercase tracking-[0.1em] transition-colors ${
            open
              ? 'text-[var(--ink)]/45 hover:text-[var(--plum)]'
              : hasPending
                ? 'text-[var(--plum)] hover:text-[var(--ink)]'
                : 'text-[var(--ink)]/45 hover:text-[var(--plum)]'
          }`}
        >
          {open ? (
            <Trans>× close</Trans>
          ) : hasPending ? (
            <Trans>review + accept ↓</Trans>
          ) : (
            <Trans>review + edit ✎</Trans>
          )}
        </button>
      </div>
      {open ? (
        <div className="mag-fade-in mt-4 border-t border-[var(--line)] pt-4">{children}</div>
      ) : (
        <div className="mt-3">{showcase}</div>
      )}
    </article>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <p className="font-serif text-[15px] italic leading-snug text-[var(--ink)]/50">{children}</p>
  );
}

// ─── Showcases ─────────────────────────────────────────────────────

function ProductsShowcase({ products }: { products: BrandBrainProduct[] }) {
  const real = products.filter((p) => p.name.trim().length > 0).slice(0, 6);
  if (real.length === 0) {
    return (
      <EmptyHint>
        <Trans>i couldn't pin down what you sell — review + edit to teach me.</Trans>
      </EmptyHint>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {real.map((p) => (
        <div
          key={p.id}
          className="rounded-xl border border-[var(--line)] bg-[var(--paper)]/50 p-3.5"
        >
          <p className="font-display text-[15px] font-bold tracking-[-0.01em] text-[var(--ink)]">
            {p.name}
          </p>
          {/* priceHint runs free-text and can be long ("USD 99/mes, IVA
              incluido — plan profesional"); give it its own line so it
              wraps instead of overflowing the card next to the name. */}
          {p.priceHint ? (
            <p className="mt-0.5 font-mono text-[10px] leading-[1.4] text-[var(--plum)]">
              {p.priceHint}
            </p>
          ) : null}
          {p.shortDescription ? (
            <p className="mt-1 text-[12.5px] leading-[1.5] text-[var(--ink)]/70">
              {p.shortDescription}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function TestimonialsShowcase({ testimonials }: { testimonials: BrandBrainTestimonial[] }) {
  const real = testimonials.filter((t) => t.quote.trim().length > 0).slice(0, 4);
  if (real.length === 0) {
    return (
      <EmptyHint>
        <Trans>no quotes i could read with confidence — review + edit to add some.</Trans>
      </EmptyHint>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {real.map((t) => (
        <figure
          key={t.id}
          className="rounded-xl border border-[var(--line)] bg-[var(--paper)]/50 p-3.5"
        >
          <blockquote className="font-serif text-[15px] leading-[1.5] text-[var(--ink)]/85">
            “{t.quote}”
          </blockquote>
          {t.attribution ? (
            <figcaption className="mt-1.5 font-mono text-[10.5px] uppercase tracking-[0.06em] text-[var(--ink)]/50">
              — {t.attribution}
            </figcaption>
          ) : null}
        </figure>
      ))}
    </div>
  );
}

function VoiceSnippetsShowcase({ snippets }: { snippets: BrandBrainVoiceExample[] }) {
  const real = snippets.filter((s) => s.text.trim().length > 0).slice(0, 6);
  if (real.length === 0) {
    return (
      <EmptyHint>
        <Trans>no standout voice snippets i caught — review + edit to add a few.</Trans>
      </EmptyHint>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {real.map((s) => (
        <span
          key={s.id}
          title={s.context ?? undefined}
          className="rounded-lg bg-[var(--spell)]/15 px-3 py-1.5 font-display text-[14px] font-semibold text-[var(--ink)]/85"
        >
          “{s.text}”
        </span>
      ))}
    </div>
  );
}

function StillReading({
  rows,
  deferred,
}: {
  rows: ExtractionStepRow[];
  deferred: ExtractionStepRow[];
}) {
  const resolved = rows.filter((s) => isResolved(s.status)).length;
  const failed = rows.filter((s) => s.status === 'failed');
  const deferredWorking = deferred.filter(
    (s) => s.status === 'pending' || s.status === 'running',
  ).length;

  return (
    <div className="rounded-2xl border-[1.5px] border-dashed border-[var(--plum)]/40 bg-white px-6 py-8">
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>mag is still reading</Trans>
        </p>
        {rows.length > 0 ? (
          <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/45">
            <Trans>
              {resolved} of {rows.length}
            </Trans>
          </p>
        ) : null}
      </div>

      <ul className="mt-4 flex flex-col gap-2.5 text-[13px] leading-relaxed text-[var(--ink)]/75">
        {rows.map((row) => (
          <li key={row.step} className="flex items-start gap-2.5">
            <span className="mt-[1px] shrink-0">
              <Indicator status={row.status} />
            </span>
            <span className="min-w-0">
              <span className={row.status === 'failed' ? 'text-[var(--ink)]/75' : undefined}>
                {STEP_LABELS[row.step] ?? row.step}
              </span>
              {/* The receipt is mag's own sentence about what she just
                  did — the whole point of surfacing this feed instead
                  of a bare spinner. Falls back to a state word only
                  when a step hasn't written one yet. */}
              <span className="text-[var(--ink)]/45">
                {' — '}
                {row.receipt ?? statusWord(row.status)}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {failed.length > 0 ? (
        <p className="mt-5 text-[12px] leading-relaxed text-[var(--ink)]/55">
          <Trans>
            some of it didn't land. what i did read is saved — you can carry on and i'll keep the
            rest for later.
          </Trans>
        </p>
      ) : null}

      <p className="mt-5 text-[12px] leading-relaxed text-[var(--ink)]/55">
        <Trans>
          this usually finishes in 30-90 seconds. you can wait here or skip to home — drafts will
          show up on the home screen as soon as they're ready.
        </Trans>
        {deferredWorking > 0 ? (
          <Trans> i'm still sorting your images in the background.</Trans>
        ) : null}
      </p>
    </div>
  );
}

function statusWord(status: ExtractionStepStatus): string {
  switch (status) {
    case 'running':
      return i18n._(msg`reading…`);
    case 'pending':
      return i18n._(msg`queued`);
    case 'skipped':
      return i18n._(msg`nothing to read`);
    case 'failed':
      return i18n._(msg`couldn't read this one`);
    default:
      return i18n._(msg`done`);
  }
}

function Indicator({ status }: { status: ExtractionStepStatus }) {
  // Four visual states, not two. The old boolean could only say
  // "done" or "spinning", which is exactly how a permanently-stuck
  // step passed for a working one.
  if (status === 'done') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-[var(--ink)] bg-[var(--spell)] text-[10px] font-bold text-[var(--ink)]"
      >
        ✓
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-[var(--ink)]/40 bg-[var(--paper)] text-[10px] font-bold text-[var(--ink)]/55"
      >
        !
      </span>
    );
  }
  if (status === 'skipped') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[var(--ink)]/20 bg-transparent text-[10px] text-[var(--ink)]/35"
      >
        –
      </span>
    );
  }
  // pending vs running: only `running` pulses, so the founder can see
  // which single step mag is on rather than a wall of identical dots.
  return (
    <span
      aria-hidden="true"
      className={
        status === 'running'
          ? 'inline-block h-4 w-4 animate-pulse rounded-full border border-[var(--plum)]/40 bg-[var(--plum)]/10'
          : 'inline-block h-4 w-4 rounded-full border border-[var(--ink)]/15 bg-transparent'
      }
    />
  );
}
